/**
 * Cinemasterpieces Home Page Animations
 * Handles bi-directional scrolling animation for hero poster grid and curated collections
 */

(function () {
  "use strict";

  /**
   * Hero Poster Grid Bi-directional Scroll Animation
   * Top row scrolls right, bottom row scrolls left
   */
  class HeroPosterScroll {
    constructor(container) {
      this.container = container;
      this.tracks = container.querySelectorAll("[data-scroll-track]");

      // Get speed from CSS variable (pixels per second)
      const speedVar = getComputedStyle(container)
        .getPropertyValue("--scroll-speed")
        .trim();
      this.speed = parseFloat(speedVar) || 30;

      this.paused = false;
      this.positions = new Map();
      this.rafId = null;
      this.manualOffset = 0;

      // Check for reduced motion preference
      this.prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      if (!this.prefersReducedMotion) {
        this.init();
      }
    }

    init() {
      // Initialize position for each track
      // Content is already duplicated in Liquid template
      this.tracks.forEach((track) => {
        // Get direction from track or parent row
        const trackDirection = track.dataset.direction;
        const row = track.closest(".hero-poster-grid__row");
        const direction = trackDirection || row?.dataset.direction || "right";

        // Store direction on track for use in animate
        track._scrollDirection = direction;

        // Calculate half width for seamless loop
        const halfWidth = track.scrollWidth / 2;

        // Set initial position
        // Right-scrolling tracks start at negative halfWidth
        // Left-scrolling tracks start at 0
        const initialPos = direction === "right" ? -halfWidth : 0;
        this.positions.set(track, initialPos);

        // Apply initial transform
        track.style.transform = `translateX(${initialPos}px)`;
      });

      // Set up manual control buttons if they exist
      this.setupManualControls();

      // Start animation loop
      this.animate();

      // Pause when page is hidden (performance optimization)
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          this.pause();
        } else {
          this.resume();
        }
      });
    }

    setupManualControls() {
      const prevBtn = this.container.querySelector("[data-scroll-prev]");
      const nextBtn = this.container.querySelector("[data-scroll-next]");

      if (prevBtn) {
        prevBtn.addEventListener("click", () => this.manualScroll("prev"));
      }

      if (nextBtn) {
        nextBtn.addEventListener("click", () => this.manualScroll("next"));
      }

      // Pause auto-scroll when hovering over controls
      const controls = this.container.querySelectorAll(".swiper-controls");
      controls.forEach((btn) => {
        btn.addEventListener("mouseenter", () => this.pause());
        btn.addEventListener("mouseleave", () => this.resume());
      });
    }

    manualScroll(direction) {
      const track = this.tracks[0];
      if (!track) return;

      // Get actual item width from first item
      const itemClass =
        this.container.dataset.sectionType === "curated-collections"
          ? ".curated-collections__item"
          : ".new-product-carousel__item";
      const firstItem = track.querySelector(itemClass);
      const itemWidth = firstItem ? firstItem.offsetWidth : 300;

      const halfWidth = track.scrollWidth / 2;
      const scrollDir = direction === "next" ? 1 : -1;

      // Get current direction from track or parent
      const trackDirection =
        track._scrollDirection ||
        track.dataset.direction ||
        track.closest("[data-direction]")?.dataset.direction ||
        "right";

      // Get current base position
      let currentPos = this.positions.get(track) || 0;

      // For curated collections or new product carousel with manual controls, use smooth CSS transition
      if (
        this.container.dataset.sectionType === "curated-collections" ||
        this.container.dataset.sectionType === "new-product-carousel"
      ) {
        track.style.transition = "transform 0.5s ease-out";

        // Silently reset position if out of bounds (without transition)
        if (trackDirection === "left") {
          if (currentPos < -halfWidth) {
            track.style.transition = "none";
            currentPos += halfWidth;
            track.style.transform = `translateX(${currentPos}px)`;
            track.offsetHeight;
            track.style.transition = "transform 0.5s ease-out";
          } else if (currentPos >= 0) {
            track.style.transition = "none";
            currentPos -= halfWidth;
            track.style.transform = `translateX(${currentPos}px)`;
            track.offsetHeight;
            track.style.transition = "transform 0.5s ease-out";
          }
        } else {
          if (currentPos >= 0) {
            track.style.transition = "none";
            currentPos -= halfWidth;
            track.style.transform = `translateX(${currentPos}px)`;
            track.offsetHeight;
            track.style.transition = "transform 0.5s ease-out";
          } else if (currentPos < -halfWidth) {
            track.style.transition = "none";
            currentPos += halfWidth;
            track.style.transform = `translateX(${currentPos}px)`;
            track.offsetHeight;
            track.style.transition = "transform 0.5s ease-out";
          }
        }

        // Now animate to new position
        let newPos = currentPos + scrollDir * itemWidth;

        this.positions.set(track, newPos);
        track.style.transform = `translateX(${newPos}px)`;

        // Remove transition after animation completes
        setTimeout(() => {
          track.style.transition = "";
          this.manualOffset = 0;
        }, 500);
      } else {
        // Original behavior for hero poster grid
        this.manualOffset += scrollDir * itemWidth;

        if (trackDirection === "right") {
          if (this.manualOffset >= halfWidth) {
            this.manualOffset = -halfWidth;
          } else if (this.manualOffset < -halfWidth) {
            this.manualOffset = 0;
          }
        } else {
          if (this.manualOffset <= -halfWidth) {
            this.manualOffset = halfWidth;
          } else if (this.manualOffset > halfWidth) {
            this.manualOffset = -halfWidth;
          }
        }
      }
    }

    pause() {
      this.paused = true;
    }

    resume() {
      this.paused = false;
    }

    animate() {
      if (!this.paused) {
        this.tracks.forEach((track) => {
          // Get direction from track (stored in init) or from dataset/parent
          const direction =
            track._scrollDirection ||
            track.dataset.direction ||
            track.closest("[data-direction]")?.dataset.direction ||
            "right";

          // Direction multiplier: right = 1, left = -1
          const multiplier = direction === "right" ? 1 : -1;

          // Current position
          let currentPos = this.positions.get(track) || 0;

          // Calculate half width for loop reset
          const halfWidth = track.scrollWidth / 2;

          // Movement per frame (normalized for ~60fps)
          // Speed is pixels per second, 0.016 is roughly 1/60
          const movement = multiplier * (this.speed * 0.016);

          // Update position with manual offset support
          let newPos = currentPos + movement;

          // Apply manual offset decay (gradually return to auto-scroll)
          if (Math.abs(this.manualOffset) > 1) {
            newPos += this.manualOffset;
            this.manualOffset *= 0.95; // Decay factor
          } else {
            this.manualOffset = 0;
          }

          // Seamless loop: reset position when reaching halfway point
          if (direction === "right" && newPos >= 0) {
            // Scrolling right: when we reach 0, reset to -halfWidth
            newPos = -halfWidth;
          } else if (direction === "left" && Math.abs(newPos) >= halfWidth) {
            // Scrolling left: when we reach -halfWidth, reset to 0
            newPos = 0;
          }

          // Save new position
          this.positions.set(track, newPos);

          // Apply transform
          track.style.transform = `translateX(${newPos}px)`;
        });
      }

      // Continue animation loop
      this.rafId = requestAnimationFrame(() => this.animate());
    }

    destroy() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
      this.tracks.forEach((track) => {
        track.style.transform = "";
      });
    }
  }

  /**
   * Initialize hero poster scroll on page load
   */
  function initHeroScroll() {
    const heroGrid = document.querySelector(
      '[data-section-type="hero-poster-grid"]',
    );
    if (heroGrid) {
      // Store instance for potential cleanup
      // heroGrid._heroPosterScroll = new HeroPosterScroll(heroGrid);
    }
  }

  /**
   * jQuery Infinite Scroll Slider Factory
   * Smooth continuous scrolling with seamless loop
   * Uses Bootstrap 5.3 classes
   * Always shows exactly 6 slides
   *
   * @param {string} selector - jQuery selector for the slider element
   * @param {string} direction - Scroll direction: 'right' or 'left'
   */
  function initSlickSlider(selector, direction) {
    const $slider = $(selector);
    if ($slider.length === 0) return;

    // Get parent container and add overflow hidden
    const $container = $slider.closest(".container-fluid");
    $container.css("overflow-x", "hidden");

    // Get the original content
    const $original = $slider.children().clone();
    const $duplicate = $original.clone();

    // Append duplicate for seamless loop
    $slider.append($duplicate);

    // Set up the slider using Bootstrap-compatible styles
    // Bootstrap row already has display:flex, just override flex-wrap
    $slider.css({
      "flex-wrap": "nowrap",
      width: "max-content",
      "will-change": "transform",
    });

    // Function to set slide widths to always show exactly 6 slides
    function setSlideWidths() {
      const containerWidth = $container[0].clientWidth;
      const slideWidth = containerWidth / 6;
      $slider.children().css({
        flex: "0 0 " + slideWidth + "px",
        "max-width": slideWidth + "px",
        width: slideWidth + "px",
      });
    }

    // Set initial slide widths
    setSlideWidths();

    // Create event namespace from selector (remove dots for valid namespace)
    const eventNamespace = selector.replace(/\./g, "");
    $(window).on(`resize.${eventNamespace}`, setSlideWidths);

    // Scroll speed (pixels per second)
    const speed = 30;

    // Calculate total width for initial position
    const totalWidth = $slider[0].scrollWidth;
    const halfWidth = totalWidth / 2;

    // Current position based on direction
    // Right scroll starts at -halfWidth, left scroll starts at 0
    let position = direction === "right" ? -halfWidth : 0;

    // Animation loop using requestAnimationFrame
    let rafId = null;
    let lastTime = performance.now();

    function animateScroll(currentTime) {
      const deltaTime = (currentTime - lastTime) / 1000; // Convert to seconds
      lastTime = currentTime;

      // Move position based on direction
      if (direction === "right") {
        position += speed * deltaTime;
        // Reset when reaching 0 for seamless loop
        if (position >= 0) {
          position = -halfWidth;
        }
      } else {
        position += speed * deltaTime;
        // Reset when reaching halfWidth for seamless loop
        if (position >= halfWidth) {
          position = 0;
        }
      }

      // Apply transform (negative for left scroll, positive for right scroll)
      const translateX = direction === "right" ? position : -position;
      $slider.css("transform", `translateX(${translateX}px)`);

      // Continue animation
      rafId = requestAnimationFrame(animateScroll);
    }

    // Start animation
    rafId = requestAnimationFrame(animateScroll);

    // Store cleanup function
    $slider.data("slickSliderCleanup", function () {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      $(window).off(`resize.${eventNamespace}`);
      // Remove duplicate content
      $slider.children().slice($original.length).remove();
      $slider.css({
        transform: "",
        "flex-wrap": "",
        width: "",
        "will-change": "",
      });
      $slider.children().css({
        flex: "",
        "max-width": "",
        width: "",
      });
      $container.css("overflow-x", "");
    });
  }

  /**
   * Initialize curated collections with embla-carousel
   */
  function initCuratedCollectionsScroll() {
    // console.log('[initCuratedCollectionsScroll] Initializing embla-carousel...');
    const curatedSection = document.querySelector(
      '[data-section-type="curated-collections"]',
    );
    // console.log('[initCuratedCollectionsScroll] Found section:', curatedSection);

    if (!curatedSection) return;

    const emblaNode = curatedSection.querySelector(".embla__viewport");
    const prevBtn = curatedSection.querySelector(".embla__prev");
    const nextBtn = curatedSection.querySelector(".embla__next");

    if (!emblaNode) {
      // console.log('[initCuratedCollectionsScroll] No embla__viewport found');
      return;
    }

    // Initialize embla-carousel with loop enabled (no auto-scroll)
    const EmblaCarousel = window.EmblaCarousel;
    if (typeof EmblaCarousel !== "function") {
      // console.log('[initCuratedCollectionsScroll] EmblaCarousel not loaded yet, retrying...');
      setTimeout(initCuratedCollectionsScroll, 100);
      return;
    }

    const emblaApi = EmblaCarousel(emblaNode, {
      loop: true,
      align: "start",
      containScroll: "trimSnaps",
    });

    curatedSection._emblaApi = emblaApi;

    // Set up navigation buttons
    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        emblaApi.scrollPrev();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        emblaApi.scrollNext();
      });
    }

    // Store cleanup function
    curatedSection._destroyEmbla = () => {
      emblaApi.destroy();
    };

    // console.log('[initCuratedCollectionsScroll] Embla initialized successfully');
  }

  /**
   * Initialize new product carousel with embla-carousel
   */
  function initNewProductCarouselScroll() {
    // console.log('[initNewProductCarouselScroll] Initializing embla-carousel...');
    const carouselSection = document.querySelector(
      '[data-section-type="new-product-carousel"]',
    );
    // console.log('[initNewProductCarouselScroll] Found section:', carouselSection);

    if (!carouselSection) return;

    const emblaNode = carouselSection.querySelector(".embla__viewport");
    const prevBtn = carouselSection.querySelector(".embla__prev");
    const nextBtn = carouselSection.querySelector(".embla__next");

    if (!emblaNode) {
      // console.log('[initNewProductCarouselScroll] No embla__viewport found');
      return;
    }

    const autoplayEnabled = carouselSection.dataset.autoplay !== "false";
    const autoplayDelay =
      parseInt(carouselSection.dataset.autoplayDelay, 10) || 3000;

    // console.log('[initNewProductCarouselScroll] autoplay:', autoplayEnabled, 'delay:', autoplayDelay);

    // Initialize embla-carousel with loop enabled
    const EmblaCarousel = window.EmblaCarousel;
    if (typeof EmblaCarousel !== "function") {
      // console.log('[initNewProductCarouselScroll] EmblaCarousel not loaded yet, retrying...');
      setTimeout(initNewProductCarouselScroll, 100);
      return;
    }

    const emblaApi = EmblaCarousel(emblaNode, {
      loop: true,
      align: "start",
      containScroll: "trimSnaps",
    });

    carouselSection._emblaApi = emblaApi;

    // Set up navigation buttons
    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        emblaApi.scrollPrev();
        if (autoplayEnabled && carouselSection._autoplayInterval) {
          clearInterval(carouselSection._autoplayInterval);
          carouselSection._autoplayInterval = null;
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        emblaApi.scrollNext();
        if (autoplayEnabled && carouselSection._autoplayInterval) {
          clearInterval(carouselSection._autoplayInterval);
          carouselSection._autoplayInterval = null;
        }
      });
    }

    // Auto-scroll functionality
    let autoplayInterval = null;

    const startAutoplay = () => {
      stopAutoplay();
      autoplayInterval = setInterval(() => {
        emblaApi.scrollNext();
      }, autoplayDelay);
    };

    const stopAutoplay = () => {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
    };

    carouselSection._startAutoplay = startAutoplay;
    carouselSection._stopAutoplay = stopAutoplay;
    carouselSection._autoplayInterval = autoplayInterval;

    // Pause on hover
    const emblaContainer = carouselSection.querySelector(".embla");
    if (emblaContainer) {
      emblaContainer.addEventListener("mouseenter", stopAutoplay);
      emblaContainer.addEventListener("mouseleave", () => {
        if (autoplayEnabled) startAutoplay();
      });
    }

    // Start autoplay if enabled
    if (autoplayEnabled) {
      startAutoplay();
    }

    // Store cleanup function
    carouselSection._destroyEmbla = () => {
      stopAutoplay();
      emblaApi.destroy();
    };

    // console.log('[initNewProductCarouselScroll] Embla initialized successfully');
  }

  /**
   * Set up manual controls only (no auto-scroll) with smooth looping
   * Supports both curated-collections and new-product-carousel sections
   */
  function setupManualControlsOnly(container) {
    // console.log('[setupManualControlsOnly] Setting up controls for section:', container.dataset.sectionType);

    const track = container.querySelector("[data-scroll-track]");
    const prevBtn = container.querySelector("[data-scroll-prev]");
    const nextBtn = container.querySelector("[data-scroll-next]");

    // console.log('[setupManualControlsOnly] track:', track, 'prevBtn:', prevBtn, 'nextBtn:', nextBtn);

    if (!track || !prevBtn || !nextBtn) return;

    // Determine item class based on section type
    const isCurated = container.dataset.sectionType === "curated-collections";
    const itemClass = isCurated
      ? ".curated-collections__item"
      : ".new-product-carousel__item";

    // Calculate content dimensions
    const items = track.querySelectorAll(itemClass);
    // console.log('[setupManualControlsOnly] itemClass:', itemClass, 'items found:', items.length);

    if (items.length < 2) {
      // console.log('[setupManualControlsOnly] Returning early - not enough items');
      return;
    }

    // Calculate width of a single set (half the items, since content is duplicated)
    const singleSetItemCount = items.length / 2;
    let singleSetWidth = 0;
    for (let i = 0; i < singleSetItemCount; i++) {
      singleSetWidth += items[i].offsetWidth;
    }

    const direction = track.dataset.direction || "left";
    const scrollAmount = items[0].offsetWidth;

    let currentPosition = 0;
    let isAnimating = false;

    // Apply smooth transition
    track.style.transition = "transform 0.5s ease-out";

    // Determine initial position based on direction
    if (direction === "right") {
      currentPosition = -singleSetWidth;
    } else {
      currentPosition = 0;
    }

    track.style.transform = `translateX(${currentPosition}px)`;

    const scroll = (btnDirection) => {
      // console.log('[scroll] Clicked:', btnDirection, 'isAnimating:', isAnimating, 'currentPosition:', currentPosition);
      if (isAnimating) return;

      const scrollDir = btnDirection === "next" ? 1 : -1;

      // First, check if we need to silently reset position to stay within bounds
      // Do this WITHOUT transition so it's invisible
      if (direction === "left") {
        if (currentPosition < -singleSetWidth) {
          // Scrolled past end - jump back by singleSetWidth
          track.style.transition = "none";
          currentPosition += singleSetWidth;
          track.style.transform = `translateX(${currentPosition}px)`;
          // Force reflow
          track.offsetHeight;
          track.style.transition = "transform 0.5s ease-out";
        } else if (currentPosition >= 0) {
          // Scrolled past start - jump forward by singleSetWidth
          track.style.transition = "none";
          currentPosition -= singleSetWidth;
          track.style.transform = `translateX(${currentPosition}px)`;
          track.offsetHeight;
          track.style.transition = "transform 0.5s ease-out";
        }
      } else {
        if (currentPosition >= 0) {
          track.style.transition = "none";
          currentPosition -= singleSetWidth;
          track.style.transform = `translateX(${currentPosition}px)`;
          track.offsetHeight;
          track.style.transition = "transform 0.5s ease-out";
        } else if (currentPosition < -singleSetWidth) {
          track.style.transition = "none";
          currentPosition += singleSetWidth;
          track.style.transform = `translateX(${currentPosition}px)`;
          track.offsetHeight;
          track.style.transition = "transform 0.5s ease-out";
        }
      }

      // Now animate to new position
      const newPosition = currentPosition + scrollDir * scrollAmount;

      isAnimating = true;
      track.style.transform = `translateX(${newPosition}px)`;

      setTimeout(() => {
        currentPosition = newPosition;
        isAnimating = false;
      }, 500);
    };

    prevBtn.addEventListener("click", () => scroll("prev"));
    nextBtn.addEventListener("click", () => scroll("next"));
  }

  function initFeaturedProductsScroll() {
    const section = document.querySelector('[data-section-type="featured-products"]');
    if (!section) return;

    if (section._destroyEmbla) section._destroyEmbla();

    const emblaNode = section.querySelector(".embla__viewport");
    const prevBtn = section.querySelector(".embla__prev");
    const nextBtn = section.querySelector(".embla__next");

    if (!emblaNode) return;

    const EmblaCarousel = window.EmblaCarousel;
    if (typeof EmblaCarousel !== "function") {
      setTimeout(initFeaturedProductsScroll, 100);
      return;
    }

    const emblaApi = EmblaCarousel(emblaNode, {
      loop: true,
      align: "start",
      containScroll: "trimSnaps",
    });

    section._emblaApi = emblaApi;

    if (prevBtn) prevBtn.addEventListener("click", () => emblaApi.scrollPrev());
    if (nextBtn) nextBtn.addEventListener("click", () => emblaApi.scrollNext());

    section._destroyEmbla = () => emblaApi.destroy();
  }

  window.initFeaturedProductsScroll = initFeaturedProductsScroll;

  function initTestimonialsSliders() {
    const sliders = document.querySelectorAll(
      '[data-section-type="testimonials-trustpilot-top"], [data-section-type="testimonials-trustpilot-bottom"]',
    );
    if (!sliders.length) return;

    const EmblaCarousel = window.EmblaCarousel;
    if (typeof EmblaCarousel !== "function") {
      setTimeout(initTestimonialsSliders, 100);
      return;
    }

    sliders.forEach((slider) => {
      if (slider._destroyEmbla) slider._destroyEmbla();

      const emblaNode = slider.querySelector(".embla__viewport");
      if (!emblaNode) return;

      const prevBtn = slider.querySelector(".embla__prev");
      const nextBtn = slider.querySelector(".embla__next");
      const autoplayEnabled = slider.dataset.autoplay !== "false";
      const autoplayDelay = parseInt(slider.dataset.autoplayDelay, 10) || 5000;

      const emblaApi = EmblaCarousel(emblaNode, {
        loop: true,
        align: "start",
        containScroll: "trimSnaps",
      });

      slider._emblaApi = emblaApi;

      let autoplayInterval = null;
      const stopAutoplay = () => {
        if (autoplayInterval) {
          clearInterval(autoplayInterval);
          autoplayInterval = null;
        }
      };
      const startAutoplay = () => {
        stopAutoplay();
        autoplayInterval = setInterval(() => emblaApi.scrollNext(), autoplayDelay);
      };

      if (prevBtn) {
        prevBtn.addEventListener("click", () => {
          emblaApi.scrollPrev();
          stopAutoplay();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener("click", () => {
          emblaApi.scrollNext();
          stopAutoplay();
        });
      }

      slider.addEventListener("mouseenter", stopAutoplay);
      slider.addEventListener("mouseleave", () => {
        if (autoplayEnabled) startAutoplay();
      });

      if (autoplayEnabled) startAutoplay();

      slider._destroyEmbla = () => {
        stopAutoplay();
        emblaApi.destroy();
      };
    });
  }

  /**
   * Initialize all scroll sections
   */
  function initAllScrollSections() {
    initSlickSlider(".slickslidertop", "right");
    initSlickSlider(".slicksliderbottom", "left");
    initCuratedCollectionsScroll();
    initNewProductCarouselScroll();
    initFeaturedProductsScroll();
    initTestimonialsSliders();
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllScrollSections);
  } else {
    initAllScrollSections();
  }

  // Shopify theme editor support
  if (Shopify.designMode) {
    document.addEventListener("shopify:section:load", function (event) {
      const section = event.target;
      if (section.dataset.sectionType === "hero-poster-grid") {
        initHeroScroll();
        initSlickSlider(".slickslidertop", "right");
        initSlickSlider(".slicksliderbottom", "left");
      } else if (section.dataset.sectionType === "curated-collections") {
        initCuratedCollectionsScroll();
      } else if (section.dataset.sectionType === "new-product-carousel") {
        initNewProductCarouselScroll();
      } else if (section.dataset.sectionType === "featured-products") {
        initFeaturedProductsScroll();
      }
    });

    document.addEventListener("shopify:section:select", function (event) {
      const section = event.target;
      if (section.dataset.sectionType === "curated-collections") {
        if (section._destroyEmbla) {
          section._destroyEmbla();
        }
        initCuratedCollectionsScroll();
      } else if (section.dataset.sectionType === "new-product-carousel") {
        if (section._destroyEmbla) {
          section._destroyEmbla();
        }
        initNewProductCarouselScroll();
      } else if (section.dataset.sectionType === "featured-products") {
        if (section._destroyEmbla) section._destroyEmbla();
        initFeaturedProductsScroll();
      }
    });

    document.addEventListener("shopify:section:unload", function (event) {
      const section = event.target;
      if (section._destroyEmbla) {
        section._destroyEmbla();
      }
      // Keep _heroPosterScroll for hero-poster-grid section only
      if (
        section.dataset.sectionType === "hero-poster-grid" &&
        section._heroPosterScroll
      ) {
        section._heroPosterScroll.destroy();
      }
    });
  }
})();
